/**
 * Cobertura del saldo (Estadísticas → Detalle técnico → «Cuenta de Twilio»): si el saldo de la cuenta de Twilio
 * alcanza para lo que las marcas tienen cargado en la plataforma. Lo calcula el backend (`coberturaSaldo` de
 * `GET /estadisticas/twilio`); acá sólo se decide el tono y se dice en palabras.
 *
 * Por qué existe: registrar una recarga en Configuración → Saldo es un asiento en el libro de la marca y NO carga plata
 * en Twilio (Twilio no tiene API para eso). Si las marcas gastan lo que cargaron y Twilio no lo tiene, Twilio se queda
 * sin saldo antes y los envíos fallan. El 12/10/2026 HATSU tenía USD 59,36 cargados y la cuenta de Twilio USD 10,65.
 *
 * El tono:
 * - «urgente» (rojo): no alcanza y Twilio ya está en el mínimo de la alerta de saldo o por debajo (el de «Alertas de
 *   gasto», USD 10 por defecto), o sin plata.
 * - «no_alcanza» (ámbar): no alcanza.
 * - «alcanza»: alcanza.
 * - «sin_comparar»: subcuenta (Twilio no informa su saldo), saldo todavía sin leer u otra moneda. Se dice lo cargado y
 *   qué mirar; nunca un «alcanza» que nadie midió.
 */

import type { CoberturaSaldo } from "../types.estadisticas";
import { usd } from "./formato";

export type TonoCobertura = "alcanza" | "no_alcanza" | "urgente" | "sin_comparar";

export interface AvisoCobertura {
  tono: TonoCobertura;
  titulo: string;
  detalle: string;
  /** decir que una recarga en la plataforma no carga Twilio y cómo se carga (cuando no alcanza, o no se sabe con una subcuenta) */
  explicarRecarga: boolean;
}

/** Lo que una persona del equipo tiene que saber cuando el saldo de Twilio no cubre lo cargado. */
export const TEXTO_RECARGA_NO_CARGA_TWILIO =
  "Una recarga en la plataforma no carga plata en Twilio. Se carga desde la consola de Twilio, o prendiendo la recarga automática.";

/** El recordatorio de Configuración → Saldo, al registrar una recarga. */
export const RECORDATORIO_RECARGA_TWILIO =
  "Esto registra la plata de la marca en la plataforma, no la carga en Twilio. Cargá lo mismo en la consola de Twilio (o prendé la recarga automática): si las marcas gastan lo que cargaron y Twilio no lo tiene, los envíos fallan.";

/** «La marca hatsu tiene cargados USD 59,36» / «Las 3 marcas que pagan con esta cuenta tienen cargados USD 84,86». */
function quienesCargaron(c: CoberturaSaldo): string {
  const n = c.marcas?.length || 0;
  const cargado = usd(c.saldoMarcasUSD);
  if (n > 1) return `Las ${n} marcas que pagan con esta cuenta tienen cargados ${cargado} en la plataforma`;
  if (n === 1) return `La marca ${c.marcas[0].brandId} tiene cargados ${cargado} en la plataforma`;
  return "Ninguna marca tiene saldo cargado en la plataforma";
}

const minuscula = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function avisoCobertura(c: CoberturaSaldo | null | undefined, saldoMinimoUSD?: number | null): AvisoCobertura | null {
  if (!c) return null;
  const hayCargado = (c.marcas?.length || 0) > 0;

  if (c.alcanza === null || c.saldoTwilioUSD === null) {
    if (c.motivo === "subcuenta") {
      return {
        tono: "sin_comparar",
        titulo: "Twilio no informa el saldo de una subcuenta",
        detalle: hayCargado
          ? `${quienesCargaron(c)}. Revisá en la consola de Twilio que la cuenta principal tenga por lo menos eso.`
          : `${quienesCargaron(c)}.`,
        explicarRecarga: hayCargado,
      };
    }
    if (c.motivo === "otra_moneda") {
      return { tono: "sin_comparar", titulo: "El saldo de Twilio no está en dólares", detalle: `${quienesCargaron(c)}: comparalo a mano en la consola de Twilio.`, explicarRecarga: false };
    }
    return {
      tono: "sin_comparar",
      titulo: "Todavía no se trajo el saldo de Twilio",
      detalle: `${quienesCargaron(c)}. Con el saldo de Twilio se ve si alcanza: tocá «Actualizar datos».`,
      explicarRecarga: false,
    };
  }

  if (c.alcanza) {
    return {
      tono: "alcanza",
      titulo: "El saldo de Twilio alcanza para lo que cargaron las marcas",
      detalle: `Twilio tiene ${usd(c.saldoTwilioUSD)}; ${minuscula(quienesCargaron(c))}.`,
      explicarRecarga: false,
    };
  }

  const enElMinimo = c.saldoTwilioUSD <= 0 || (typeof saldoMinimoUSD === "number" && Number.isFinite(saldoMinimoUSD) && c.saldoTwilioUSD <= saldoMinimoUSD);
  return {
    tono: enElMinimo ? "urgente" : "no_alcanza",
    titulo: "El saldo de Twilio no alcanza para lo que cargaron las marcas",
    detalle:
      `${quienesCargaron(c)} y Twilio tiene ${usd(c.saldoTwilioUSD)}: faltan ${usd(c.faltaUSD)}.` +
      (enElMinimo
        ? c.saldoTwilioUSD <= 0
          ? " Twilio está sin saldo: hasta que se cargue, los envíos pueden fallar."
          : ` Además ya está en el mínimo de la alerta de saldo (${usd(saldoMinimoUSD)}): si se queda sin plata, los envíos fallan.`
        : ""),
    explicarRecarga: true,
  };
}
